import * as React from 'react';
import styled from '@emotion/styled';
import { useFormik } from 'formik';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { BasicPageWidget } from '../../widgets/layout/BasicPageWidget';
import { InputContainerWidget } from '../../widgets/forms/InputContainerWidget';
import { TextInputType, TextInputWidget } from '../../widgets/forms/TextInputWidget';
import { ButtonType, ButtonWidget } from '../../widgets/controls/ButtonWidget';
import { useSystem } from '../../hooks/useSystem';
import { observer } from 'mobx-react';
import { FooterWidget } from '../../widgets/layout/FooterWidget';
import { Notification, NotificationType } from '../../core/stores/NotificationStore';
import { RouteError } from '@foxbun/lib-errors';
import { AuthCodes } from '@foxbun/lib-goferal-types';
import { FormValues } from './RegisterPage';
import { Routing } from '../Routing';

const logo = require('../../../media/logo.png');

export const ResetPasswordPage: React.FC = observer(() => {
  const system = useSystem();
  const [searchParams] = useSearchParams();
  const form = useFormik<{ password: string; password_verify: string }>({
    initialValues: {
      password: null,
      password_verify: null
    },
    isInitialValid: false,
    validate: async (params) => {
      let errors = ['password', 'password_verify'].reduce((prev, cur) => {
        if (!params[cur]) {
          prev[cur] = 'required';
        }
        return prev;
      }, {} as Partial<FormValues>);

      if (params.password && params.password.length < 8) {
        errors.password = 'password must be 8 chars or more';
      }
      if (params.password !== params.password_verify) {
        errors.password_verify = 'Passwords dont match';
      }
      if (Object.keys(errors).length > 0) {
        return errors;
      }
      return false;
    },
    onSubmit: async ({ password }) => {
      try {
        //send to server
        await system.auth_client.resetPassword({
          password,
          code: searchParams.get('code')
        });
        system.notification_store.showNotification(
          new Notification({
            type: NotificationType.SUCCESS,
            desc: 'Password reset successfully, please sign in.'
          })
        );
        navigate(Routing.HOME);
      } catch (ex) {
        if (ex instanceof RouteError && ex.error.code === AuthCodes.INVALID_PASSWORD) {
          form.setErrors({
            password: 'Not strong enough'
          });
          system.notification_store.showNotification(
            new Notification({
              type: NotificationType.ERROR,
              desc: ex.error.human_error
            })
          );
        } else {
          system.notification_store.showNotification(
            new Notification({
              type: NotificationType.ERROR,
              desc: 'Failed to reset password, please try again later.'
            })
          );
          console.error('failed to reset password', ex);
        }
      }
    }
  });
  const navigate = useNavigate();
  if (system.user) {
    return <Navigate to={Routing.HOME} />;
  }

  return (
    <BasicPageWidget>
      <S.Container>
        <S.Logo src={logo} />
        <InputContainerWidget label="Password" error={form.errors.password}>
          <TextInputWidget
            type={TextInputType.PASS}
            value={form.values.password}
            onChange={form.getFieldHelpers('password').setValue}
          />
        </InputContainerWidget>
        <InputContainerWidget label="Password Verify" error={form.errors.password_verify}>
          <TextInputWidget
            type={TextInputType.PASS}
            value={form.values.password_verify}
            onChange={form.getFieldHelpers('password_verify').setValue}
          />
        </InputContainerWidget>
        <S.Buttons>
          <ButtonWidget
            disabled={!form.isValid}
            loading={form.isSubmitting}
            label="Reset password"
            action={() => {
              form.submitForm();
            }}
          />
          <ButtonWidget
            type={ButtonType.SECONDARY}
            label="Back"
            action={() => {
              navigate(Routing.HOME);
            }}
          />
        </S.Buttons>
      </S.Container>
      <FooterWidget />
    </BasicPageWidget>
  );
});
namespace S {
  export const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 10px;
    justify-content: center;
    height: 100%;
  `;

  export const Buttons = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    align-items: stretch;
    width: 250px;
  `;

  export const Logo = styled.img`
    width: 300px;
  `;
}
